import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Classroom } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Button, Modal, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useState } from "react";

interface ClassroomDeletionFormProps {
	classroom: Classroom | null;
	showModal: boolean;
	setShowModal: (show: boolean) => void;
	onDeletion?: () => void;
}

const ClassroomDeletionModal: React.FC<ClassroomDeletionFormProps> = ({
	classroom,
	showModal,
	setShowModal,
	onDeletion,
}) => {
	const [loading, setLoading] = useState<boolean>(false);

	const session = useContext(SessionContext);
	if (!session) {
		throw new Error("AdminClassroomDeletionForm must be a child of SessionContext.");
	}
	const token = session.token;
	if (!token) return null;

	// Set up the form; we only need one field where the user confirms the classroom name.
	const form = useForm({
		initialValues: {
			classroomName: "",
		},
		validate: {
			classroomName: (value) =>
				value.trim().length === 0
					? "You must type the classroom name to confirm deletion."
					: classroom !== null && value.trim() !== classroom.name.trim()
						? "Entered classroom name does not match."
						: null,
		},
	});

	const handleSubmit = async () => {
		if (!form.isValid()) {
			form.validate();
			return;
		}

		if (!classroom) {
			return;
		}

		setLoading(true);

		// Display a notification that deletion is in progress.
		const notificationId = Utils.displayNotification(
			"Deleting Classroom",
			"Deleting classroom, please wait...",
			"blue",
			"loading"
		);

		try {
			// Call the API to delete the classroom.
			await ConductionAPI.deleteClassroom(classroom.id, token);

			Utils.updateNotification(
				notificationId,
				"Classroom Deleted",
				"The classroom was deleted successfully.",
				"green",
				<IconCheck size={16} />
			);

			if (onDeletion) {
				onDeletion();
			}
			setShowModal(false);
		} catch (error) {
			console.error("Error deleting classroom", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to delete the classroom.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal opened={showModal} onClose={() => setShowModal(false)} title="Delete Classroom" size="lg">
			{loading ? (
				<Skeleton height="100%" radius="md" />
			) : classroom === null ? (
				<Text>Classroom data did not load correctly.</Text>
			) : (
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack>
						<Text>
							Please type the exact name of the classroom, "{classroom.name}", to confirm deletion:
						</Text>
						<TextInput
							label="Classroom Name"
							placeholder={classroom.name}
							required
							{...form.getInputProps("classroomName")}
						/>
						<Button type="submit" radius="xl" size="sm" loading={form.submitting} color="red">
							Delete Classroom
						</Button>
					</Stack>
				</form>
			)}
		</Modal>
	);
};

export default ClassroomDeletionModal;
